import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LockIcon, MinusIcon, PlusIcon, ShoppingBagIcon, Trash2Icon, XIcon } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { store } from "../data/store";
import { formatPrice } from "../utils/currency";
const ease = [0.23, 1, 0.32, 1] as const;
export function CartDrawer() {
  const {
    items,
    subtotal,
    isOpen,
    closeCart,
    updateQty,
    removeItem
  } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const remaining = Math.max(0, store.freeShippingThreshold - subtotal);
  const progress = Math.min(100, subtotal / store.freeShippingThreshold * 100);
  useEffect(() => {
    if (!isOpen) {
      setCheckingOut(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeCart();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeCart]);
  return <AnimatePresence data-id="element-39" data-mp="42xresfo9bk0">
      {isOpen && <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Shopping cart" data-id="element-40" data-mp="3v98wcznsywgm">
          <motion.div className="absolute inset-0 bg-black/70" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={closeCart} data-id="element-41" data-mp="cvjayar4nm9w" />
          <motion.aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-line bg-surface" initial={{
        x: "100%"
      }} animate={{
        x: 0
      }} exit={{
        x: "100%"
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-42" data-mp="mv0eommtjq38">
            <div className="flex items-center justify-between border-b border-line px-6 py-5" data-id="element-43" data-mp="2xl8ngh4cyvzf">
              <h2 className="font-display text-xl font-semibold" data-id="element-44" data-mp="2xn1u3wez1g7f">Your cart</h2>
              <button type="button" onClick={closeCart} className="rounded-full p-2 transition-colors hover:bg-raised" aria-label="Close cart" data-id="element-45" data-mp="2tlga7r40s1nq">
                <XIcon className="h-5 w-5" data-id="element-46" data-mp="16h6qjrzlr3j4" />
              </button>
            </div>

            {items.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-47" data-mp="1bgt6waaq8zw5">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-raised" data-id="element-48" data-mp="1fieq0dduuaes">
                  <ShoppingBagIcon className="h-6 w-6" aria-hidden="true" data-id="element-49" data-mp="1kggan2qxihsd" />
                </div>
                <p className="mt-5 font-display text-lg font-semibold" data-id="element-50" data-mp="1p750kdmyn6pe">Your cart is empty</p>
                <p className="mt-1 text-sm text-muted" data-id="element-51" data-mp="1u7auk0gpd5an">Find something to awaken the passion.</p>
                <Link to="/shop" onClick={closeCart} className="mt-6 rounded-full bg-brand px-6 py-3 text-sm font-bold text-night transition-colors hover:bg-brand-dark" data-id="element-52" data-mp="1z649zyfjv6dg">
                  Browse products
                </Link>
              </div> : <>
                <div className="border-b border-line px-6 py-4" data-id="element-53" data-mp="3jmlim0nb9s1v">
                  <p className="text-sm" data-id="element-54" data-mp="3eo0lia3cvohn">
                    {remaining > 0 ? <>
                        Add <strong className="text-brand" data-id="element-55" data-mp="39r0m2i10hjzi">{formatPrice(remaining)}</strong> for free shipping
                      </> : <strong className="text-brand" data-id="element-56" data-mp="698q6sjr8fw1">You've unlocked free shipping</strong>}
                  </p>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-raised" data-id="element-57" data-mp="1h8l6lotuwsl">
                    <div className="h-full rounded-full bg-brand transition-[width] duration-300 ease-brand" style={{
                width: `${progress}%`
              }} data-id="element-58" data-mp="3spcxkafeb5y9" />
                  </div>
                </div>

                <ul className="flex-1 divide-y divide-line overflow-y-auto px-6" data-id="element-59" data-mp="p61ialh6raos">
                  {items.map(({
              product,
              qty
            }) => <li key={product.slug} className="flex gap-4 py-5" data-id="element-60" data-mp="drorc8gxw0ui">
                      <img src={product.images[0]} alt="" className="h-20 w-20 shrink-0 rounded-xl bg-white object-contain" data-id="element-61" data-mp="3vx1gz196lrt8" />
                      <div className="flex flex-1 flex-col" data-id="element-62" data-mp="sft1up5u9627">
                        <div className="flex items-start justify-between gap-3" data-id="element-63" data-mp="sifg17n8ijte">
                          <div data-id="element-64" data-mp="xemfchl4kx2d">
                            <p className="text-xs text-subtle" data-id="element-65" data-mp="iqj6n0fp779v">{product.brand}</p>
                            <p className="font-display font-semibold leading-snug" data-id="element-66" data-mp="iof34jlpoaa4">{product.name}</p>
                          </div>
                          <p className="font-semibold" data-id="element-67" data-mp="1beotptu858cv">{formatPrice(product.price * qty)}</p>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-2" data-id="element-68" data-mp="1g6ypvrlxn4nl">
                          <div className="flex items-center rounded-full border border-line" data-id="element-69" data-mp="30viy1kmho65v">
                            <button type="button" onClick={() => updateQty(product.slug, qty - 1)} className="p-2 text-muted hover:text-white" aria-label={`Decrease quantity of ${product.name}`} data-id="element-70" data-mp="2vy0zmo63l9c4">
                              <MinusIcon className="h-3.5 w-3.5" data-id="element-71" data-mp="2hxq4va1o3alo" />
                            </button>
                            <span className="w-6 text-center text-sm font-medium" aria-live="polite" data-id="element-72" data-mp="2d0garh3c2ari">
                              {qty}
                            </span>
                            <button type="button" onClick={() => updateQty(product.slug, qty + 1)} className="p-2 text-muted hover:text-white" aria-label={`Increase quantity of ${product.name}`} data-id="element-73" data-mp="2lya6oqx6r2e5">
                              <PlusIcon className="h-3.5 w-3.5" data-id="element-74" data-mp="2zzvn9a58bi5u" />
                            </button>
                          </div>
                          <button type="button" onClick={() => removeItem(product.slug)} className="rounded-full p-2 text-subtle transition-colors hover:bg-raised hover:text-white" aria-label={`Remove ${product.name}`} data-id="element-75" data-mp="3418u9nk25rec">
                            <Trash2Icon className="h-4 w-4" data-id="element-76" data-mp="2g9jytybxwmt6" />
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>

                <div className="border-t border-line px-6 py-5" data-id="element-77" data-mp="1smlg0u0cl2s3">
                  <div className="flex items-center justify-between" data-id="element-78" data-mp="1sk94ayl6nonh">
                    <span className="text-sm text-muted" data-id="element-79" data-mp="1xk3suvhhk25d">Subtotal</span>
                    <span className="font-display text-xl font-semibold" data-id="element-80" data-mp="1q2fc2mbqhcbj">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-subtle" data-id="element-81" data-mp="1l443eex6d7uw">Taxes included. Shipping calculated at checkout.</p>
                  <button type="button" onClick={() => setCheckingOut(true)} disabled={checkingOut} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-sm font-bold text-night transition-colors hover:bg-brand-dark disabled:opacity-70" data-id="element-82" data-mp="1g25178d68gzj">
                    <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-83" data-mp="xe9si2wf1tda" />
                    {checkingOut ? "Opening secure checkout…" : "Secure checkout"}
                  </button>
                  <p className="mt-3 text-center text-xs text-subtle" data-id="element-84" data-mp="od1yvy4uhgpj">Ships in plain, unbranded packaging</p>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}